import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Modal,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    ApartmentOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    RobotOutlined,
    StopOutlined,
    ThunderboltOutlined,
    WifiOutlined,
} from '@ant-design/icons';
import OperationTimeline from './OperationTimeline';
import {
    operationStatusColor,
    opMissionStatusColor,
    OPERATION_STATUS_OPTIONS,
    TRIGGER_TYPE_OPTIONS,
    type OperationItem,
    type OperationMissionItem,
} from './operationModels';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useAuth } from '../../context/AuthContext';
import '../../style/Operations.css';

const { Text, Title } = Typography;

const formatTimestamp = (value?: string | null) => {
    if (!value) return <Text type="secondary">—</Text>;
    const parsed = dayjs(value);
    if (!parsed.isValid()) return <Text type="secondary">Unknown</Text>;
    return <Text title={parsed.toISOString()}>{parsed.format('DD MMM YYYY HH:mm')}</Text>;
};

const isDeviceOnline = (device: { lastHeartbeat?: string | null; currentStatus?: string | null }) => {
    if (device.currentStatus === 'online') return true;
    if (!device.lastHeartbeat) return false;
    const parsed = new Date(device.lastHeartbeat).getTime();
    return Number.isFinite(parsed) && Date.now() - parsed < 60000;
};

const OperationDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [operation, setOperation] = React.useState<OperationItem | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [actionLoading, setActionLoading] = React.useState(false);

    const operationId = React.useMemo(() => {
        const num = Number(id);
        return Number.isFinite(num) && num > 0 ? num : null;
    }, [id]);

    const loadOperation = React.useCallback(async () => {
        if (!operationId) return;
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.OPERATION_DETAIL(operationId), { withCredentials: true });
            setOperation(res.data?.data || res.data || null);
        } catch (err: any) {
            console.error('Failed to load operation', err);
            message.error(err?.response?.data?.message || 'Failed to load operation');
            setOperation(null);
        } finally {
            setLoading(false);
        }
    }, [operationId]);

    React.useEffect(() => {
        void loadOperation();
    }, [loadOperation]);

    const handleStart = React.useCallback(async () => {
        if (!operationId) return;
        setActionLoading(true);
        try {
            await axios.post(ENDPOINTS.OPERATION_START(operationId), {}, { withCredentials: true });
            message.success('Operation sent to robot');
            void loadOperation();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to send operation');
        } finally {
            setActionLoading(false);
        }
    }, [operationId, loadOperation]);

    const handleCancel = React.useCallback(async () => {
        if (!operationId) return;
        setActionLoading(true);
        try {
            await axios.post(ENDPOINTS.OPERATION_CANCEL(operationId), {}, { withCredentials: true });
            message.success('Operation cancelled');
            void loadOperation();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to cancel operation');
        } finally {
            setActionLoading(false);
        }
    }, [operationId, loadOperation]);

    const handleDelete = React.useCallback(() => {
        if (!operationId) return;
        Modal.confirm({
            title: 'Delete operation',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to delete this operation? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await axios.delete(ENDPOINTS.OPERATION_DETAIL(operationId), { withCredentials: true });
                    message.success('Operation deleted');
                    navigate('/operations');
                } catch (err: any) {
                    message.error(err?.response?.data?.message || 'Failed to delete operation');
                }
            },
        });
    }, [operationId, navigate]);

    // ── Computed stats ───────────────────────────────────────────────────────
    const missions = operation?.operationMissions || [];
    const sortedMissions = React.useMemo(
        () => [...missions].sort((a, b) => a.executionOrder - b.executionOrder),
        [missions],
    );

    const stats = React.useMemo(() => {
        const total = missions.length;
        const completed = missions.filter((m) => m.status === 'completed').length;
        const running = missions.filter((m) => m.status === 'running' || m.status === 'dispatched').length;
        const failed = missions.filter((m) => m.status === 'failed').length;
        const pending = missions.filter((m) => m.status === 'pending' || m.status === 'ready').length;
        const measurements = operation?.measurementCount ?? 0;
        return { total, completed, running, failed, pending, measurements };
    }, [missions, operation?.measurementCount]);

    const involvedDevices = React.useMemo(() => {
        const byId = new Map<number, {
            id: number;
            label: string;
            currentStatus?: string | null;
            lastHeartbeat?: string | null;
            deviceTypeName?: string | null;
            missionCount: number;
            activeCount: number;
            measurementCount: number;
            lastMeasurementAt?: string | null;
        }>();

        sortedMissions.forEach((om) => {
            const device = om.device || om.mission?.device;
            const deviceId = device?.id || om.mission?.deviceId;
            if (!deviceId) return;

            const existing = byId.get(deviceId) || {
                id: deviceId,
                label: device?.label || `Device #${deviceId}`,
                currentStatus: device?.currentStatus || null,
                lastHeartbeat: device?.lastHeartbeat || null,
                deviceTypeName: device?.deviceType?.name || null,
                missionCount: 0,
                activeCount: 0,
                measurementCount: 0,
                lastMeasurementAt: null,
            };

            existing.missionCount += 1;
            if (om.status === 'running' || om.status === 'dispatched') {
                existing.activeCount += 1;
            }
            existing.measurementCount += om.measurementCount || 0;
            if (om.lastMeasurementAt && (!existing.lastMeasurementAt || dayjs(om.lastMeasurementAt).isAfter(dayjs(existing.lastMeasurementAt)))) {
                existing.lastMeasurementAt = om.lastMeasurementAt;
            }
            byId.set(deviceId, existing);
        });

        return Array.from(byId.values());
    }, [sortedMissions]);

    const missionDetailPath = React.useCallback((missionId: unknown) => {
        const parsed = Number(missionId);
        if (!Number.isFinite(parsed) || parsed <= 0) return '';
        return `/operations/missions/${parsed}?from=operation&operationId=${operationId}`;
    }, [operationId]);

    // ── Mission steps columns ────────────────────────────────────────────────
    const missionColumns = React.useMemo<ColumnsType<OperationMissionItem>>(() => [
        {
            title: '#',
            dataIndex: 'executionOrder',
            key: 'executionOrder',
            width: 60,
            render: (val: number) => <Tag color="blue">#{val}</Tag>,
        },
        {
            title: 'Mission',
            key: 'mission',
            render: (_, om) => {
                const detailPath = missionDetailPath(om.missionId);
                const label = om.mission?.name || `Mission #${om.missionId}`;
                return (
                <Space direction="vertical" size={2}>
                    {detailPath ? (
                        <Button
                            type="link"
                            size="small"
                            className="operations-inline-link"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(detailPath);
                            }}
                        >
                            {label}
                        </Button>
                    ) : (
                        <Text strong>{label}</Text>
                    )}
                    {om.mission?.description && (
                        <Text type="secondary" style={{ fontSize: 11 }}>{om.mission.description}</Text>
                    )}
                </Space>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (val: string) => <Tag color={opMissionStatusColor(val)}>{val}</Tag>,
        },
        {
            title: 'Device',
            key: 'device',
            width: 150,
            render: (_, om) => {
                const label = om.device?.label || om.mission?.device?.label;
                return label ? (
                    <Space size={4}>
                        <RobotOutlined style={{ fontSize: 12 }} />
                        <Text>{label}</Text>
                    </Space>
                ) : (
                    <Text type="secondary">—</Text>
                );
            },
        },
        {
            title: 'Measurements',
            key: 'measurements',
            width: 150,
            render: (_, om) => (
                <Space direction="vertical" size={2}>
                    <Text>{om.measurementCount ?? 0}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {om.lastMeasurementAt ? dayjs(om.lastMeasurementAt).format('DD MMM YYYY HH:mm') : 'No readings'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Trigger',
            key: 'trigger',
            width: 110,
            render: (_, om) => {
                const triggerVal = (om as any).triggerCondition || (om as any).triggerType || 'auto';
                const trigger = TRIGGER_TYPE_OPTIONS.find((t) => t.value === triggerVal);
                return <Tag>{trigger?.label || triggerVal}</Tag>;
            },
        },
        {
            title: 'Dependencies',
            key: 'deps',
            width: 130,
            render: (_, om) => {
                const deps = om.dependsOnOperationMissionIds || [];
                if (deps.length === 0) return <Text type="secondary">None</Text>;
                return (
                    <Space size={4} wrap>
                        {deps.map((d) => (
                            <Tag key={d} color="geekblue">#{d}</Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Started',
            key: 'startedAt',
            width: 150,
            render: (_, om) => formatTimestamp(om.startedAt),
        },
        {
            title: 'Completed',
            key: 'completedAt',
            width: 150,
            render: (_, om) => formatTimestamp(om.completedAt),
        },
    ], [missionDetailPath, navigate]);

    // ── Render ───────────────────────────────────────────────────────────────
    if (!operationId) {
        return (
            <div className="operations-page">
                <Alert type="error" showIcon message="Invalid operation ID" />
            </div>
        );
    }

    return (
        <div className="operations-page">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Back navigation */}
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    className="operations-back-button"
                    onClick={() => navigate('/operations')}
                >
                    Back to Operations
                </Button>

                <Spin spinning={loading}>
                    {!operation ? (
                        loading ? null : (
                            <Empty description="Operation not found" />
                        )
                    ) : (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {/* ── Header ────────────────────────────────────────── */}
                            <Row justify="space-between" align="top" gutter={[16, 16]}>
                                <Col flex="auto">
                                    <Space direction="vertical" size={4}>
                                        <Space align="center" size={12}>
                                            <ApartmentOutlined style={{ fontSize: 24, color: '#1677ff' }} />
                                            <Title level={3} style={{ margin: 0 }}>
                                                {operation.name}
                                            </Title>
                                            <Tag
                                                color={operationStatusColor(operation.status)}
                                                style={{ fontSize: 13, padding: '2px 10px' }}
                                            >
                                                {operation.status}
                                            </Tag>
                                        </Space>
                                        {operation.description && (
                                            <Text type="secondary" style={{ marginLeft: 36 }}>
                                                {operation.description}
                                            </Text>
                                        )}
                                    </Space>
                                </Col>
                                <Col>
                                    <Space wrap>
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={() => void loadOperation()}
                                            loading={loading}
                                        >
                                            Refresh
                                        </Button>
                                        {(operation.status === 'draft' || operation.status === 'planned') && (
                                            <Button
                                                type="primary"
                                                icon={<PlayCircleOutlined />}
                                                onClick={() => void handleStart()}
                                                loading={actionLoading}
                                            >
                                                Send
                                            </Button>
                                        )}
                                        {(operation.status === 'planned' || operation.status === 'dispatching' || operation.status === 'running') && (
                                            <Button
                                                danger
                                                icon={<StopOutlined />}
                                                onClick={() => void handleCancel()}
                                                loading={actionLoading}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                        <Button
                                            danger
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            onClick={handleDelete}
                                        >
                                            Delete
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>

                            {/* ── Stats Cards ───────────────────────────────────── */}
                            <Row gutter={[12, 12]}>
                                <Col xs={12} md={6} xl={4}>
                                    <Card size="small" className="operation-detail-stat-card">
                                        <Statistic
                                            title="Total missions"
                                            value={stats.total}
                                            prefix={<ApartmentOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} md={6} xl={4}>
                                    <Card size="small" className="operation-detail-stat-card">
                                        <Statistic
                                            title="Completed"
                                            value={stats.completed}
                                            valueStyle={{ color: '#722ed1' }}
                                            prefix={<CheckCircleOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} md={6} xl={4}>
                                    <Card size="small" className="operation-detail-stat-card">
                                        <Statistic
                                            title="Running"
                                            value={stats.running}
                                            valueStyle={{ color: '#52c41a' }}
                                            prefix={<ThunderboltOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} md={6} xl={4}>
                                    <Card size="small" className="operation-detail-stat-card">
                                        <Statistic
                                            title="Failed / Pending"
                                            value={stats.failed}
                                            suffix={`/ ${stats.pending}`}
                                            valueStyle={stats.failed > 0 ? { color: '#ff4d4f' } : undefined}
                                            prefix={stats.failed > 0 ? <CloseCircleOutlined /> : <ClockCircleOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} md={6} xl={4}>
                                    <Card size="small" className="operation-detail-stat-card">
                                        <Statistic
                                            title="Measurements"
                                            value={stats.measurements}
                                            prefix={<ThunderboltOutlined />}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* ── Involved Devices ─────────────────────────────── */}
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <RobotOutlined />
                                        <span>Devices involved</span>
                                        <Tag>{involvedDevices.length}</Tag>
                                    </Space>
                                }
                                className="operation-detail-devices-card"
                            >
                                {involvedDevices.length === 0 ? (
                                    <Empty
                                        description="No devices linked to this operation"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                ) : (
                                    <Row gutter={[12, 12]}>
                                        {involvedDevices.map((device) => {
                                            const online = isDeviceOnline(device);
                                            return (
                                                <Col xs={24} sm={12} lg={8} xl={6} key={device.id}>
                                                    <Card
                                                        size="small"
                                                        hoverable
                                                        className={`operation-device-card ${online ? 'operation-device-card-online' : 'operation-device-card-offline'}`}
                                                        onClick={() => navigate(`/operations/device/${device.id}`)}
                                                        title={
                                                            <Space>
                                                                <Badge status={online ? 'success' : 'default'} />
                                                                <RobotOutlined />
                                                                <Text strong style={{ fontSize: 13 }}>{device.label}</Text>
                                                            </Space>
                                                        }
                                                        extra={
                                                            <Tag color={online ? 'green' : 'default'}>
                                                                {online ? 'Online' : 'Offline'}
                                                            </Tag>
                                                        }
                                                    >
                                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                            <Space size={4} wrap>
                                                                {device.deviceTypeName ? (
                                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                                        {device.deviceTypeName}
                                                                    </Text>
                                                                ) : null}
                                                                {device.currentStatus ? (
                                                                    <Tag style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                                                                        {device.currentStatus}
                                                                    </Tag>
                                                                ) : null}
                                                            </Space>
                                                            <Text style={{ fontSize: 11 }}>
                                                                <ApartmentOutlined /> {device.missionCount} step{device.missionCount !== 1 ? 's' : ''}
                                                                {device.activeCount > 0 ? `, ${device.activeCount} active` : ''}
                                                            </Text>
                                                            <Text style={{ fontSize: 11 }}>
                                                                <ThunderboltOutlined /> {device.measurementCount} reading{device.measurementCount !== 1 ? 's' : ''}
                                                            </Text>
                                                            {device.lastHeartbeat ? (
                                                                <Text type="secondary" style={{ fontSize: 10 }}>
                                                                    <WifiOutlined /> Last seen: {dayjs(device.lastHeartbeat).format('HH:mm:ss')}
                                                                </Text>
                                                            ) : (
                                                                <Text type="secondary" style={{ fontSize: 10 }}>
                                                                    <WifiOutlined /> No heartbeat
                                                                </Text>
                                                            )}
                                                            <Button
                                                                size="small"
                                                                type="primary"
                                                                ghost
                                                                block
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    navigate(`/operations/device/${device.id}`);
                                                                }}
                                                            >
                                                                Open live view
                                                            </Button>
                                                        </Space>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                )}
                            </Card>

                            {/* ── Operation Details ─────────────────────────────── */}
                            <Card
                                size="small"
                                title="Operation details"
                                className="operation-detail-info-card"
                            >
                                <Descriptions
                                    size="small"
                                    column={{ xs: 1, md: 2, xl: 3 }}
                                    bordered
                                >
                                    <Descriptions.Item label="Name">
                                        {operation.name}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">
                                        <Tag color={operationStatusColor(operation.status)}>
                                            {operation.status}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Area">
                                        {operation.area?.name || (operation.areaId ? `Area #${operation.areaId}` : '—')}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Scheduled">
                                        {formatTimestamp(operation.scheduledStartAt || operation.scheduledAt)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Started">
                                        {formatTimestamp(operation.startedAt)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Completed">
                                        {formatTimestamp(operation.completedAt)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Created">
                                        {formatTimestamp(operation.createdAt)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Updated">
                                        {formatTimestamp(operation.updatedAt)}
                                    </Descriptions.Item>
                                    {operation.metadata?.operationType && (
                                        <Descriptions.Item label="Type">
                                            <Tag>{operation.metadata.operationType}</Tag>
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                                {operation.description && (
                                    <div style={{ marginTop: 12 }}>
                                        <Text type="secondary">{operation.description}</Text>
                                    </div>
                                )}
                            </Card>

                            {/* ── Mission Timeline ──────────────────────────────── */}
                            {sortedMissions.length > 0 && (
                                <Card
                                    size="small"
                                    title={
                                        <Space>
                                            <ApartmentOutlined />
                                            <span>Execution timeline</span>
                                            <Tag>{sortedMissions.length} steps</Tag>
                                        </Space>
                                    }
                                    className="operation-detail-timeline-card"
                                >
                                    <div style={{ padding: '12px 0', overflowX: 'auto' }}>
                                        <OperationTimeline operationMissions={sortedMissions} />
                                    </div>
                                </Card>
                            )}

                            {/* ── Mission Steps Table ───────────────────────────── */}
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <RobotOutlined />
                                        <span>Mission steps</span>
                                        <Tag>{sortedMissions.length}</Tag>
                                    </Space>
                                }
                                className="operation-detail-missions-card"
                            >
                                <Table<OperationMissionItem>
                                    rowKey="id"
                                    columns={missionColumns}
                                    dataSource={sortedMissions}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 1050 }}
                                    locale={{
                                        emptyText: (
                                            <Empty
                                                description="No mission steps in this operation"
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            />
                                        ),
                                    }}
                                    rowClassName={(om) => {
                                        if (om.status === 'running' || om.status === 'dispatched') {
                                            return 'operation-detail-mission-row-active';
                                        }
                                        if (om.status === 'completed') {
                                            return 'operation-detail-mission-row-completed';
                                        }
                                        if (om.status === 'failed') {
                                            return 'operation-detail-mission-row-failed';
                                        }
                                        return '';
                                    }}
                                />
                            </Card>
                        </Space>
                    )}
                </Spin>
            </Space>
        </div>
    );
};

export default OperationDetailView;
